import axios from 'axios';
import {
    BarChart3,
    Edit,
    FileEdit,
    FileText,
    FolderKanban,
    Image,
    LayoutDashboard,
    LogOut,
    MapPin,
    MessageSquare,
    Package,
    Plus,
    Trash2
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import BrochureManager from './BrochureManager';
import ContentManager from './ContentManager';
import SliderManager from './SliderManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [projects, setProjects] = useState([]);
  const [products, setProducts] = useState([]);
  const [statistics, setStatistics] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [brochures, setBrochures] = useState([]);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);

  const [projectForm, setProjectForm] = useState({
    name: '',
    location: '',
    capacity: '',
    capacity_value: 0,
    scope: [],
    description: '',
    latitude: 20.5937,
    longitude: 78.9629,
    image_url: '',
    year: new Date().getFullYear(),
    status: 'completed',
    turbine_type: ''
  });

  const [productForm, setProductForm] = useState({
    name: '',
    category: '',
    description: '',
    features: [],
    applications: [],
    image_url: '',
    technical_specs: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, productsRes, statsRes, contactsRes, brochuresRes] = await Promise.all([
        axios.get(`${API}/projects`),
        axios.get(`${API}/products`),
        axios.get(`${API}/statistics`),
        axios.get(`${API}/contacts`),
        axios.get(`${API}/brochures`)
      ]);
      setProjects(projectsRes.data);
      setProducts(productsRes.data);
      setStatistics(statsRes.data);
      setContacts(contactsRes.data);
      setBrochures(brochuresRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    try {
      const scopeArray = typeof projectForm.scope === 'string' ? projectForm.scope.split(',').map(s => s.trim()) : projectForm.scope;
      const data = { ...projectForm, scope: scopeArray };
      
      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.project_id}`, data);
        toast.success('Project updated successfully');
      } else {
        await axios.post(`${API}/projects`, data);
        toast.success('Project created successfully');
      }
      setShowProjectForm(false);
      setEditingProject(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save project');
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      const featuresArray = typeof productForm.features === 'string' ? productForm.features.split(',').map(s => s.trim()) : productForm.features;
      const applicationsArray = typeof productForm.applications === 'string' ? productForm.applications.split(',').map(s => s.trim()) : productForm.applications;
      const data = { ...productForm, features: featuresArray, applications: applicationsArray };
      
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.product_id}`, data);
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/products`, data);
        toast.success('Product created successfully');
      }
      setShowProductForm(false);
      setEditingProduct(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const handleDeleteProject = async (id) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await axios.delete(`${API}/projects/${id}`);
        toast.success('Project deleted');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete project');
      }
    }
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await axios.delete(`${API}/products/${id}`);
        toast.success('Product deleted');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete product');
      }
    }
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setProjectForm({
      name: project.name,
      location: project.location,
      capacity: project.capacity,
      capacity_value: project.capacity_value,
      scope: project.scope,
      description: project.description,
      latitude: project.latitude,
      longitude: project.longitude,
      image_url: project.image_url,
      year: project.year,
      status: project.status,
      turbine_type: project.turbine_type || ''
    });
    setShowProjectForm(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      category: product.category,
      description: product.description,
      features: product.features,
      applications: product.applications,
      image_url: product.image_url,
      technical_specs: product.technical_specs
    });
    setShowProductForm(true);
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="admin-dashboard">
      <div className="flex">
        <aside className="w-64 bg-slate-900 border-r border-white/10 min-h-screen fixed" data-testid="admin-sidebar">
          <div className="p-6">
            <img
              src="/images/ytek-logo.png"
              alt="Ytek Controls"
              className="h-12 w-auto mb-2"
            />
            <p className="text-xs text-slate-500 uppercase tracking-wider">Admin Portal</p>
          </div>
          <nav className="px-4">
            <button
              onClick={() => setActiveTab('dashboard')}
              data-testid="nav-dashboard"
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors mb-1 ${
                activeTab === 'dashboard' ? 'bg-ocean-500 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <LayoutDashboard size={18} />
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('slider')}
              data-testid="nav-slider"
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors mb-1 ${
                activeTab === 'slider' ? 'bg-ocean-500 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <Image size={18} />
              Hero Slider
            </button>
            <button
              onClick={() => setActiveTab('content')}
              data-testid="nav-content"
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors mb-1 ${
                activeTab === 'content' ? 'bg-ocean-500 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <FileEdit size={18} />
              Site Content
            </button>
            <button
              onClick={() => setActiveTab('projects')}
              data-testid="nav-projects"
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors mb-1 ${
                activeTab === 'projects' ? 'bg-ocean-500 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <FolderKanban size={18} />
              Projects
            </button>
            <button
              onClick={() => setActiveTab('products')}
              data-testid="nav-products"
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors mb-1 ${
                activeTab === 'products' ? 'bg-ocean-500 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <Package size={18} />
              Products
            </button>
            <button
              onClick={() => setActiveTab('statistics')}
              data-testid="nav-statistics"
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors mb-1 ${
                activeTab === 'statistics' ? 'bg-ocean-500 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <BarChart3 size={18} />
              Statistics
            </button>
            <button
              onClick={() => setActiveTab('brochures')}
              data-testid="nav-brochures"
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors mb-1 ${
                activeTab === 'brochures' ? 'bg-ocean-500 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <FileText size={18} />
              Brochures
            </button>
            <button
              onClick={() => setActiveTab('contacts')}
              data-testid="nav-contacts"
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors mb-1 ${
                activeTab === 'contacts' ? 'bg-ocean-500 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <MessageSquare size={18} />
              Contacts
            </button>
          </nav>
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
            >
              <LogOut size={18} />
              Logout
            </button>
            <Link
              to="/"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 mt-2 text-sm border border-white/10 hover:border-ocean-500 text-slate-400 hover:text-white transition-colors"
            >
              View Website
            </Link>
          </div>
        </aside>

        <main className="flex-1 ml-64 p-8">
          {activeTab === 'dashboard' && (
            <div data-testid="dashboard-content">
              <h1 className="font-heading text-3xl font-medium text-white mb-8">Dashboard Overview</h1>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-slate-900 border border-white/10 p-6">
                  <p className="text-sm text-slate-400 mb-2">Total Projects</p>
                  <p className="text-3xl font-bold text-white">{projects.length}</p>
                </div>
                <div className="bg-slate-900 border border-white/10 p-6">
                  <p className="text-sm text-slate-400 mb-2">Total Products</p>
                  <p className="text-3xl font-bold text-white">{products.length}</p>
                </div>
                <div className="bg-slate-900 border border-white/10 p-6">
                  <p className="text-sm text-slate-400 mb-2">Contact Submissions</p>
                  <p className="text-3xl font-bold text-white">{contacts.length}</p>
                </div>
                <div className="bg-slate-900 border border-white/10 p-6">
                  <p className="text-sm text-slate-400 mb-2">Brochures</p>
                  <p className="text-3xl font-bold text-white">{brochures.length}</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'slider' && <SliderManager />}

          {activeTab === 'content' && <ContentManager />}

          {activeTab === 'projects' && (
            <div data-testid="projects-content">
              <div className="flex justify-between items-center mb-8">
                <h1 className="font-heading text-3xl font-medium text-white">Manage Projects</h1>
                <button
                  onClick={() => {
                    setShowProjectForm(true);
                    setEditingProject(null);
                    setProjectForm({
                      name: '',
                      location: '',
                      capacity: '',
                      capacity_value: 0,
                      scope: [],
                      description: '',
                      latitude: 20.5937,
                      longitude: 78.9629,
                      image_url: '',
                      year: new Date().getFullYear(),
                      status: 'completed',
                      turbine_type: ''
                    });
                  }}
                  data-testid="add-project-button"
                  className="px-6 py-3 bg-ocean-500 hover:bg-ocean-600 text-white font-medium uppercase tracking-widest text-xs transition-all inline-flex items-center gap-2"
                >
                  <Plus size={16} />
                  Add Project
                </button>
              </div>

              {showProjectForm && (
                <div className="bg-slate-900 border border-white/10 p-8 mb-8" data-testid="project-form">
                  <h2 className="text-xl font-semibold text-white mb-6">
                    {editingProject ? 'Edit Project' : 'Add New Project'}
                  </h2>
                  <form onSubmit={handleProjectSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="Project Name"
                        value={projectForm.name}
                        onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                        required
                        data-testid="project-input-name"
                        className="bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 px-4 text-white placeholder:text-slate-500 outline-none"
                      />
                      <input
                        type="text"
                        placeholder="Location"
                        value={projectForm.location}
                        onChange={(e) => setProjectForm({ ...projectForm, location: e.target.value })}
                        required
                        data-testid="project-input-location"
                        className="bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 px-4 text-white placeholder:text-slate-500 outline-none"
                      />
                      <input
                        type="text"
                        placeholder="Capacity (e.g., 5 MW)"
                        value={projectForm.capacity}
                        onChange={(e) => setProjectForm({ ...projectForm, capacity: e.target.value })}
                        required
                        data-testid="project-input-capacity"
                        className="bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 px-4 text-white placeholder:text-slate-500 outline-none"
                      />
                      <input
                        type="number"
                        step="0.1"
                        placeholder="Capacity Value (numeric)"
                        value={projectForm.capacity_value}
                        onChange={(e) => setProjectForm({ ...projectForm, capacity_value: parseFloat(e.target.value) })}
                        required
                        data-testid="project-input-capacity-value"
                        className="bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 px-4 text-white placeholder:text-slate-500 outline-none"
                      />
                      <input
                        type="number"
                        placeholder="Year"
                        value={projectForm.year}
                        onChange={(e) => setProjectForm({ ...projectForm, year: parseInt(e.target.value) })}
                        required
                        data-testid="project-input-year"
                        className="bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 px-4 text-white placeholder:text-slate-500 outline-none"
                      />
                      <select
                        value={projectForm.status}
                        onChange={(e) => setProjectForm({ ...projectForm, status: e.target.value })}
                        data-testid="project-input-status"
                        className="bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 px-4 text-white outline-none"
                      >
                        <option value="completed">Completed</option>
                        <option value="ongoing">Ongoing</option>
                      </select>
                      <select
                        value={projectForm.turbine_type}
                        onChange={(e) => setProjectForm({ ...projectForm, turbine_type: e.target.value })}
                        data-testid="project-input-turbine-type"
                        className="bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 px-4 text-white outline-none"
                      >
                        <option value="">Select Turbine Type</option>
                        <option value="francis">Francis</option>
                        <option value="kaplan">Kaplan</option>
                        <option value="pelton">Pelton</option>
                        <option value="crossflow">Crossflow</option>
                      </select>
                      <input
                        type="number"
                        step="0.0001"
                        placeholder="Latitude"
                        value={projectForm.latitude}
                        onChange={(e) => setProjectForm({ ...projectForm, latitude: parseFloat(e.target.value) })}
                        required
                        data-testid="project-input-latitude"
                        className="bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 px-4 text-white placeholder:text-slate-500 outline-none"
                      />
                      <input
                        type="number"
                        step="0.0001"
                        placeholder="Longitude"
                        value={projectForm.longitude}
                        onChange={(e) => setProjectForm({ ...projectForm, longitude: parseFloat(e.target.value) })}
                        required
                        data-testid="project-input-longitude"
                        className="bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 px-4 text-white placeholder:text-slate-500 outline-none"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Scope (comma-separated: Design, Control Panels, SCADA)"
                      value={Array.isArray(projectForm.scope) ? projectForm.scope.join(', ') : projectForm.scope}
                      onChange={(e) => setProjectForm({ ...projectForm, scope: e.target.value })}
                      required
                      data-testid="project-input-scope"
                      className="w-full bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 px-4 text-white placeholder:text-slate-500 outline-none"
                    />
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Upload Project Image</label>
                      <div className="flex gap-4">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            const uploadFormData = new FormData();
                            uploadFormData.append('image', file);
                            try {
                              const response = await axios.post(`${API}/upload/image`, uploadFormData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              setProjectForm({ ...projectForm, image_url: response.data.image_url });
                              toast.success('Image uploaded');
                            } catch (error) {
                              toast.error('Failed to upload image');
                            }
                          }}
                          className="flex-1 bg-slate-900/50 border border-white/10 focus:border-ocean-500 p-3 text-white outline-none"
                        />
                      </div>
                      {projectForm.image_url && (
                        <div className="mt-2">
                          <img src={projectForm.image_url} alt="Preview" className="h-32 object-cover border border-white/10" />
                        </div>
                      )}
                    </div>
                    <textarea
                      placeholder="Project Description"
                      value={projectForm.description}
                      onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                      required
                      rows="3"
                      data-testid="project-input-description"
                      className="w-full bg-slate-900/50 border border-white/10 focus:border-ocean-500 p-4 text-white placeholder:text-slate-500 outline-none resize-none"
                    />
                    <div className="flex gap-4">
                      <button
                        type="submit"
                        data-testid="project-submit-button"
                        className="px-6 py-3 bg-ocean-500 hover:bg-ocean-600 text-white font-medium uppercase tracking-widest text-xs transition-all"
                      >
                        {editingProject ? 'Update' : 'Create'} Project
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowProjectForm(false);
                          setEditingProject(null);
                        }}
                        data-testid="project-cancel-button"
                        className="px-6 py-3 border border-white/20 text-slate-400 hover:text-white hover:border-white font-medium uppercase tracking-widest text-xs transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="space-y-4">
                {projects.map((project) => (
                  <div
                    key={project.project_id}
                    data-testid={`project-item-${project.project_id}`}
                    className="bg-slate-900 border border-white/10 p-6 flex items-center justify-between"
                  >
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">{project.name}</h3>
                      <div className="flex items-center gap-4 text-sm text-slate-400">
                        <span className="flex items-center gap-1">
                          <MapPin size={14} />
                          {project.location}
                        </span>
                        <span>{project.capacity}</span>
                        <span>{project.year}</span>
                        <span className="px-2 py-1 bg-slate-800 text-xs uppercase">{project.status}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditProject(project)}
                        data-testid={`project-edit-${project.project_id}`}
                        className="p-2 border border-white/10 hover:border-ocean-500 text-slate-400 hover:text-white transition-colors"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteProject(project.project_id)}
                        data-testid={`project-delete-${project.project_id}`}
                        className="p-2 border border-white/10 hover:border-red-500 text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'products' && (
            <div data-testid="products-content">
              <div className="flex justify-between items-center mb-8">
                <h1 className="font-heading text-3xl font-medium text-white">Manage Products</h1>
                <button
                  onClick={() => {
                    setShowProductForm(true);
                    setEditingProduct(null);
                    setProductForm({
                      name: '',
                      category: '',
                      description: '',
                      features: [],
                      applications: [],
                      image_url: '',
                      technical_specs: ''
                    });
                  }}
                  data-testid="add-product-button"
                  className="px-6 py-3 bg-ocean-500 hover:bg-ocean-600 text-white font-medium uppercase tracking-widest text-xs transition-all inline-flex items-center gap-2"
                >
                  <Plus size={16} />
                  Add Product
                </button>
              </div>

              {showProductForm && (
                <div className="bg-slate-900 border border-white/10 p-8 mb-8" data-testid="product-form">
                  <h2 className="text-xl font-semibold text-white mb-6">
                    {editingProduct ? 'Edit Product' : 'Add New Product'}
                  </h2>
                  <form onSubmit={handleProductSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="Product Name"
                        value={productForm.name}
                        onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                        required
                        data-testid="product-input-name"
                        className="bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 px-4 text-white placeholder:text-slate-500 outline-none"
                      />
                      <input
                        type="text"
                        placeholder="Category"
                        value={productForm.category}
                        onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                        required
                        data-testid="product-input-category"
                        className="bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 px-4 text-white placeholder:text-slate-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Upload Product Image</label>
                      <div className="flex gap-4">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            const uploadFormData = new FormData();
                            uploadFormData.append('image', file);
                            try {
                              const response = await axios.post(`${API}/upload/image`, uploadFormData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              setProductForm({ ...productForm, image_url: response.data.image_url });
                              toast.success('Image uploaded');
                            } catch (error) {
                              toast.error('Failed to upload image');
                            }
                          }}
                          className="flex-1 bg-slate-900/50 border border-white/10 focus:border-ocean-500 p-3 text-white outline-none"
                        />
                      </div>
                      {productForm.image_url && (
                        <div className="mt-2">
                          <img src={productForm.image_url} alt="Preview" className="h-32 object-cover border border-white/10" />
                        </div>
                      )}
                    </div>
                    <textarea
                      placeholder="Description"
                      value={productForm.description}
                      onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                      required
                      rows="3"
                      data-testid="product-input-description"
                      className="w-full bg-slate-900/50 border border-white/10 focus:border-ocean-500 p-4 text-white placeholder:text-slate-500 outline-none resize-none"
                    />
                    <input
                      type="text"
                      placeholder="Features (comma-separated)"
                      value={Array.isArray(productForm.features) ? productForm.features.join(', ') : productForm.features}
                      onChange={(e) => setProductForm({ ...productForm, features: e.target.value })}
                      required
                      data-testid="product-input-features"
                      className="w-full bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 px-4 text-white placeholder:text-slate-500 outline-none"
                    />
                    <input
                      type="text"
                      placeholder="Applications (comma-separated)"
                      value={Array.isArray(productForm.applications) ? productForm.applications.join(', ') : productForm.applications}
                      onChange={(e) => setProductForm({ ...productForm, applications: e.target.value })}
                      required
                      data-testid="product-input-applications"
                      className="w-full bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 px-4 text-white placeholder:text-slate-500 outline-none"
                    />
                    <textarea
                      placeholder="Technical Specifications"
                      value={productForm.technical_specs}
                      onChange={(e) => setProductForm({ ...productForm, technical_specs: e.target.value })}
                      required
                      rows="2"
                      data-testid="product-input-specs"
                      className="w-full bg-slate-900/50 border border-white/10 focus:border-ocean-500 p-4 text-white placeholder:text-slate-500 outline-none resize-none"
                    />
                    <div className="flex gap-4">
                      <button
                        type="submit"
                        data-testid="product-submit-button"
                        className="px-6 py-3 bg-ocean-500 hover:bg-ocean-600 text-white font-medium uppercase tracking-widest text-xs transition-all"
                      >
                        {editingProduct ? 'Update' : 'Create'} Product
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowProductForm(false);
                          setEditingProduct(null);
                        }}
                        data-testid="product-cancel-button"
                        className="px-6 py-3 border border-white/20 text-slate-400 hover:text-white hover:border-white font-medium uppercase tracking-widest text-xs transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="space-y-4">
                {products.map((product) => (
                  <div
                    key={product.product_id}
                    data-testid={`product-item-${product.product_id}`}
                    className="bg-slate-900 border border-white/10 p-6 flex items-center justify-between"
                  >
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">{product.name}</h3>
                      <p className="text-sm text-slate-400 mb-2">{product.description}</p>
                      <span className="px-2 py-1 bg-slate-800 text-xs uppercase text-slate-300">
                        {product.category}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditProduct(product)}
                        data-testid={`product-edit-${product.product_id}`}
                        className="p-2 border border-white/10 hover:border-ocean-500 text-slate-400 hover:text-white transition-colors"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(product.product_id)}
                        data-testid={`product-delete-${product.product_id}`}
                        className="p-2 border border-white/10 hover:border-red-500 text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'statistics' && (
            <div data-testid="statistics-content">
              <h1 className="font-heading text-3xl font-medium text-white mb-8">Statistics</h1>
              <p className="text-slate-400 mb-4">Manage the counter statistics shown on the homepage.</p>
              <div className="space-y-4">
                {statistics.map((stat) => (
                  <div
                    key={stat.stat_id}
                    data-testid={`stat-item-${stat.stat_id}`}
                    className="bg-slate-900 border border-white/10 p-6"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-2xl font-bold text-white">
                          {stat.value}
                          {stat.suffix}
                        </p>
                        <p className="text-sm text-slate-400 uppercase tracking-wider">{stat.label}</p>
                      </div>
                      <p className="text-slate-500 text-sm">Order: {stat.order}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'brochures' && <BrochureManager />}

          {activeTab === 'contacts' && (
            <div data-testid="contacts-content">
              <h1 className="font-heading text-3xl font-medium text-white mb-8">Contact Submissions</h1>
              <div className="space-y-4">
                {contacts.map((contact) => (
                  <div
                    key={contact.contact_id}
                    data-testid={`contact-item-${contact.contact_id}`}
                    className="bg-slate-900 border border-white/10 p-6"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-white">{contact.name}</h3>
                        <p className="text-sm text-slate-400">{contact.company}</p>
                      </div>
                      <p className="text-xs text-slate-500">
                        {new Date(contact.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="space-y-2 text-sm text-slate-400">
                      <p>Email: {contact.email}</p>
                      <p>Phone: {contact.phone}</p>
                      <p className="mt-3 text-white">{contact.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
